'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useParams, useSearchParams } from 'next/navigation';
import gsap from 'gsap';
import { CustomEase } from 'gsap/CustomEase';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { useGSAP } from '@gsap/react';
import { useUi } from '@weareconceptstudio/core';
import { setSmoother } from './gsapSmootherInstance';

//! Register GSAP plugins once
gsap.registerPlugin(ScrollTrigger, ScrollSmoother, ScrollToPlugin, CustomEase);

export default function ScrollWrapper({ children }) {
	const { isBurgerMenuOpen, popupIsOpen, winWidth } = useUi();

	const wrapperRef = useRef(null);
	const contentRef = useRef(null);
	const smoother = useRef(null);
	const [isHydrated, setIsHydrated] = useState(false);

	const pathname = usePathname();
	const params = useParams();
	const searchParams = useSearchParams();

	//! Set hydration state
	useEffect(() => {
		setIsHydrated(true);
	}, []);

	//! Scroll to top on route change
	useEffect(() => {
		if (smoother.current) {
			smoother.current.scrollTop(0);
		}
	}, [pathname, params?.lang, searchParams.get('categoryId'), searchParams.get('page')]);

	//! Initialize ScrollSmoother safely
	useGSAP(
		() => {
			if (!isHydrated) return;

			const wrapper = wrapperRef.current;
			const content = contentRef.current;
			const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

			if (!isTouchDevice && wrapper instanceof Element && content instanceof Element) {
				smoother.current = ScrollSmoother.create({
					wrapper,
					content,
					smooth: 0.7,
					speed: 1,
					ease: CustomEase.create('easeOutExpo', 'M0,0 C0.19,1,0.22,1,1,1'),
					normalizeScroll: true,
					effects: true,
				});

				setSmoother(smoother.current);
			}
		},
		{ dependencies: [isHydrated, pathname] }
	);

	//! Pause smoother on popup or burger menu open
	useEffect(() => {
		if (!smoother.current) return;

		const shouldPause = winWidth >= 768 ? popupIsOpen : popupIsOpen || isBurgerMenuOpen;
		smoother.current.paused(shouldPause);
	}, [popupIsOpen, isBurgerMenuOpen, winWidth]);

	return (
		<div
			ref={wrapperRef}
			id='smooth-wrapper'
		>
			<div
				ref={contentRef}
				id='smooth-content'
			>
				{children}
			</div>
		</div>
	);
}
